import { useMutation, useQueryClient } from "@tanstack/react-query";
import { USER_ROLES } from "@/constants/auth";
import { queryKeys } from "@/constants/queryKeys";
import { useCustomerScopedQuery } from "@/hooks/useCustomerScopedQuery";
import { useToast } from "@/hooks/useToast";
import useAuthStore from "@/context/auth/useAuthStore";
import { services } from "@/services/registry";
export const useOrganizationUsers = (enabled = true)=>{
    const { userRole } = useAuthStore();
    const canViewMembers = userRole === USER_ROLES.VIEWER || userRole === USER_ROLES.ADMIN || userRole === USER_ROLES.SUPERADMIN;
    return useCustomerScopedQuery({
        queryKey: queryKeys.organizations.users(),
        queryFn: ({ customerId })=>services.organizationService.getOrganizationUsers(undefined, customerId),
        staleTime: 5 * 60 * 1000,
        enabled: enabled && canViewMembers
    });
};
export const useAddOrganizationUser = ()=>{
    const queryClient = useQueryClient();
    const toast = useToast();
    return useMutation({
        mutationFn: (formData)=>services.organizationService.addUser(formData),
        onSuccess: ()=>{
            queryClient.invalidateQueries({
                queryKey: queryKeys.organizations.users()
            });
            toast.success("User added successfully");
        },
        onError: (error)=>{
            toast.handleApiError(error, "Failed to add user");
        }
    });
};
export const useDeleteOrganizationUser = ()=>{
    const queryClient = useQueryClient();
    const toast = useToast();
    return useMutation({
        mutationFn: (userId)=>services.organizationService.deleteUser(userId),
        onSuccess: ()=>{
            queryClient.invalidateQueries({
                queryKey: queryKeys.organizations.users()
            });
            toast.success("User removed successfully");
        },
        onError: (error)=>{
            toast.handleApiError(error, "Failed to remove user");
        }
    });
};
export const useUpdateOrganizationName = (customerId)=>{
    const queryClient = useQueryClient();
    const toast = useToast();
    const organizationUsersQueryKey = queryKeys.organizations.users(customerId);
    return useMutation({
        mutationFn: (name)=>services.organizationService.updateOrganizationName(name),
        onMutate: async (name)=>{
            const trimmedName = name.trim();
            await queryClient.cancelQueries({
                queryKey: organizationUsersQueryKey
            });
            const previousOrganizationUsers = queryClient.getQueryData(organizationUsersQueryKey);
            queryClient.setQueryData(organizationUsersQueryKey, (currentData)=>{
                if (!currentData) {
                    return currentData;
                }
                return {
                    ...currentData,
                    organizationName: trimmedName
                };
            });
            return {
                previousOrganizationUsers
            };
        },
        onSuccess: (response)=>{
            queryClient.setQueryData(organizationUsersQueryKey, (currentData)=>{
                if (!currentData) {
                    return currentData;
                }
                return {
                    ...currentData,
                    organizationName: response.organization.Name,
                    organizationEmail: currentData.organizationEmail ?? response.organization.Email
                };
            });
            toast.success("Organization name updated successfully");
        },
        onError: (error, _name, context)=>{
            if (context?.previousOrganizationUsers) {
                queryClient.setQueryData(organizationUsersQueryKey, context.previousOrganizationUsers);
            }
            toast.handleApiError(error, "Failed to update organization name");
        },
        onSettled: ()=>{
            queryClient.invalidateQueries({
                queryKey: organizationUsersQueryKey
            });
        }
    });
};
export const useOrganization = (enabled = true)=>{
    const usersQuery = useOrganizationUsers(enabled);
    const addUserMutation = useAddOrganizationUser();
    const deleteUserMutation = useDeleteOrganizationUser();
    const updateNameMutation = useUpdateOrganizationName();
    return {
        users: usersQuery.data?.users || [],
        userLimit: usersQuery.data?.userLimit || 50,
        organizationName: usersQuery.data?.organizationName,
        organizationEmail: usersQuery.data?.organizationEmail,
        isLoading: usersQuery.isLoading,
        error: usersQuery.error,
        refetch: usersQuery.refetch,
        addUser: addUserMutation.mutateAsync,
        deleteUser: deleteUserMutation.mutateAsync,
        updateOrganizationName: updateNameMutation.mutateAsync,
        isAddingUser: addUserMutation.isPending,
        isDeletingUser: deleteUserMutation.isPending,
        isUpdatingName: updateNameMutation.isPending,
        addUserError: addUserMutation.error,
        deleteUserError: deleteUserMutation.error,
        updateNameError: updateNameMutation.error
    };
};
